import { ReactNode, useState } from "react";

import get from "lodash.get";
import { produce } from "immer";
import { AnimatePresence } from "framer-motion";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";

import { t } from "@lingui/macro";
import { Plus } from "@phosphor-icons/react";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import {
  defaultAward,
  defaultCertification,
  defaultCustomSection,
  defaultEducation,
  defaultExperience,
  defaultInterest,
  defaultLanguage,
  defaultProfile,
  defaultProject,
  defaultPublication,
  defaultReference,
  defaultSkill,
  defaultVolunteer,
  type SectionItem,
  type SectionKey,
  type SectionWithItem,
} from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import { createId } from "@paralleldrive/cuid2";
import { Button, Tooltip } from "@reactive-resume/ui";
import { EyeSlash } from "@phosphor-icons/react/dist/ssr";

import { CollapsibleSection } from "../../../CollapsibleSection";

import { SectionIcon } from "./section-icon";
import { SectionOptions } from "./section-options";
import { SectionListItem } from "./section-list-item";

type Props<T extends SectionItem> = {
  id: SectionKey;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
};

export const SectionBase = <T extends SectionItem>({ id, title, description }: Props<T>) => {
  const { open } = useDialog(id);

  // State to manage which item is currently expanded (accordion behavior)
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) =>
    get(state.resume.data.sections, id),
  ) as SectionWithItem<T>;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!section) return null;

  const onDragStart = () => {
    // Collapse all expanded items when drag starts for better UX
    setExpandedItemId(null);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
      setValue(`sections.${id}.items`, sortedList);
    }
  };

  // Create a mapping from section key → default item object
  const defaultItemsMap: Partial<Record<SectionKey, SectionItem>> = {
    experience: defaultExperience,
    education: defaultEducation,
    skills: defaultSkill,
    projects: defaultProject,
    awards: defaultAward,
    custom: defaultCustomSection,
    certifications: defaultCertification,
    volunteer: defaultVolunteer,
    interests: defaultInterest,
    languages: defaultLanguage,
    publications: defaultPublication,
    profiles: defaultProfile,
    references: defaultReference,
  };

  const titleFieldMap: Partial<Record<SectionKey, string>> = {
    experience: "company",
    education: "institution",
    skills: "name",
    projects: "name",
    awards: "title",
    custom: "name",
    certifications: "name",
    volunteer: "organization",
    interests: "name",
    languages: "name",
    profiles: "network",
    publications: "name",
    references: "name",
  };

  // handles on click of "Add new item button"
  const onCreate = () => {
    if (!section) return;
    const currSectionId = id.includes("custom") ? "custom" : id;

    const defaultItem = defaultItemsMap[currSectionId];
    const titleField = titleFieldMap[currSectionId];

    open("create", { id });
    setValue(
      `sections.${id}.items`,
      produce(section.items, (draft: T[]): void => {
        const { id, ...rest } = defaultItem as T;
        const count = draft.length + 1;

        const newItem = {
          ...rest,
          id: createId(),
        } as any;

        if (titleField) {
          newItem[titleField] = `${section.name} ${count}`;
        }

        draft.push(newItem as T);
      }),
    );
  };

  const onUpdate = (item: T) => {
    open("update", { id, item });
  };

  // handles on click of "copy" within the item
  const onDuplicate = (item: T) => {
    if (!section) return;
    open("duplicate", { id, item });

    const duplicatedItem = { ...item, id: createId() } as T;

    setValue(
      `sections.${id}.items`,
      produce(section.items, (draft: T[]): void => {
        draft.push(duplicatedItem);
      }),
    );
  };

  // handles when an item is removed - clear expanded state if needed
  const onItemRemoved = (itemId: string) => {
    if (expandedItemId === itemId) {
      setExpandedItemId(null);
    }
  };

  // helper function to check if the section should be default open/collapsed
  const getDefaultOpenStatus = (id: string) => {
    const defaultOpenSectionIds = ["experience", "education", "skills"];
    return defaultOpenSectionIds.includes(id);
  };

  const headerHTML: ReactNode = (
    <div className="flex w-full justify-between">
      <div className="flex items-center gap-x-4">
        <SectionIcon id={id} size={18} />
        <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-xl">
          {section.name.charAt(0).toUpperCase() + section.name.slice(1).toLowerCase()}
        </h2>
      </div>

      <div className="flex items-center gap-1">
        {/* Show EyeSlash icon if section not visible */}
        {!section.visible && (
          <Tooltip content="This section is not visible">
            <div onClick={(e) => e.stopPropagation()} className="text-red-500 opacity-70">
              <EyeSlash size={18} />
            </div>
          </Tooltip>
        )}
        <div onClick={(e) => e.stopPropagation()}>
          <SectionOptions id={id} />
        </div>
      </div>
    </div>
  );

  const contentHTML: ReactNode = (
    <div className="flex flex-col gap-4">
      <main className={cn("grid transition-opacity", !section.visible && "opacity-50")}>
        {section.items.length === 0 && (
          <Button
            variant="outline"
            className="gap-x-2 border-dashed py-6 leading-relaxed hover:bg-secondary-accent"
            onClick={onCreate}
          >
            <Plus size={14} />
            <span className="font-medium">
              {t({
                message: section.name.toLowerCase().includes("custom")
                  ? "Add a new item"
                  : "Add a new " + section.name,
                context: "For example, add a new work experience, or add a new profile.",
              })}
            </span>
          </Button>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={section.items} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {section.items.map((item, index) => (
                <SectionListItem
                  key={item.id}
                  id={item.id}
                  itemIndex={index}
                  sectionId={id}
                  visible={item.visible}
                  canAICustomize={item.canAICustomize}
                  title={title(item as T)}
                  description={description?.(item as T)}
                  isExpanded={expandedItemId === item.id}
                  onToggleExpand={(itemId: string) => {
                    setExpandedItemId(expandedItemId === itemId ? null : itemId);
                  }}
                  onItemRemoved={onItemRemoved}
                  onUpdate={() => {
                    onUpdate(item as T);
                  }}
                  onDuplicate={() => {
                    onDuplicate(item as T);
                  }}
                />
              ))}
            </AnimatePresence>
          </SortableContext>
        </DndContext>
      </main>

      {/* Add a new item button for each section */}
      {section.items.length > 0 && (
        <>
          <footer className="flex items-center justify-end">
            <Button
              // variant="outline"
              className="gap-x-2 rounded-2xl bg-[#047857] text-xs text-white hover:bg-[#045436] lg:text-sm"
              onClick={onCreate}
            >
              <Plus />
              {/* <span>Add {section.name}</span> */}
              <span>
                Add {section.name.charAt(0).toUpperCase() + section.name.slice(1).toLowerCase()}
              </span>
            </Button>
          </footer>
          {/* {showAddNewItem && <CustomSectionDialog />} */}
        </>
      )}
    </div>
  );

  return (
    <CollapsibleSection
      id={`section-base-${id}`}
      defaultOpen={getDefaultOpenStatus(id)}
      header={headerHTML}
      content={contentHTML}
      isVisible={section.visible}
    />
  );
};
